import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";
import { v4 as uuid } from "uuid";
import { lib, PBKDF2, algo, AES, mode, pad, enc } from "crypto-js";

export const SESSION_EXPIRED_KEY = "sessionExpired";

let handlingSessionExpiry = false;

function isCognitoAuthError(error) {
  const name = error?.name || "";
  const message = String(error?.message || "");
  return (
    name === "NotAuthorizedException" ||
    /invalid login token|couldn't verify signed token|refresh.*fail/i.test(message)
  );
}

export function isInvalidLoginResponse(response) {
  if (!response) return false;
  if (response.status === 401) return true;
  const errType = response.headers.get("x-amzn-errortype") || "";
  if (errType.includes("NotAuthorizedException")) return true;
  const msg = response.headers.get("x-amzn-errormessage") || "";
  return /invalid login token|couldn't verify signed token/i.test(msg);
}

export function safePostLoginPath(path) {
  const raw = path || "/";
  const pathname = raw.split(/[?#]/)[0] || "/";
  if (
    pathname.startsWith("/search/") ||
    pathname.startsWith("/screening/") ||
    pathname.startsWith("/login")
  ) {
    return "/";
  }
  return raw;
}

function postLoginPathAfterExpiry() {
  if (typeof window === "undefined") return "/";
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return safePostLoginPath(path);
}

export async function handleSessionExpired() {
  if (handlingSessionExpiry) return;
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  handlingSessionExpiry = true;
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch (_) { /* ignore quota / private mode */ }
  try {
    await signOut();
  } catch (error) {
    console.error("Error signing out after session expiry:", error);
  }
  const redirect = encodeURIComponent(postLoginPathAfterExpiry());
  window.location.assign(`/login?redirect=${redirect}`);
}

async function authorizedFetch(url, options) {
  const response = await fetch(url, options);
  if (isInvalidLoginResponse(response)) {
    await handleSessionExpired();
    throw new Error("SESSION_EXPIRED");
  }
  return response;
}

async function getToken() {
  try {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    if (idToken) return idToken;
  } catch (error) {
    console.error(error);
    if (isCognitoAuthError(error)) {
      await handleSessionExpired();
      throw error;
    }
  }
  try {
    await getCurrentUser();
    await handleSessionExpired();
    throw new Error("SESSION_EXPIRED");
  } catch (error) {
    if (error?.message === "SESSION_EXPIRED") throw error;
    if (isCognitoAuthError(error)) {
      await handleSessionExpired();
      throw error;
    }
  }
  try {
    return await getTempToken();
  } catch (error) {
    console.error("Error fetching temp token:", error);
  }
}

async function getTempToken() {
  try {
    // get device id
    const deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      throw new Error("Device ID not found in local storage");
    }
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/one-time`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": deviceId,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data.token;
  } catch (error) {
    console.error("Error fetching temp token:", error);
    throw error;
  }
}

export const scheduleSearch = async (
  question,
  filters = {},
  pico = {},
  query = "",
  searchMode = "public",
  frequency
) => {
  try {
    const idToken = await getToken();
    const requestBody = {
      question,
      filters,
      pico,
      query,
      searchMode,
      frequency,
    };
    await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/scheduleSearch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ requestBody }),
    });
  } catch (error) {
    console.error("Error fetching UUID:", error);
  }
};

export const search = async (
  question,
  filters = {},
  pico = {},
  query = "",
  searchMode = "public"
) => {
  try {
    const idToken = await getToken();
    const requestBody = {
      question,
      filters,
      pico,
      query,
      searchMode,
    };
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/search`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ requestBody }),
      }
    );

    if (response.status === 403) {
      const errorData = await response.json();
      // Save form state before returning error for login redirect
      saveUserSearchTerms(question, filters, pico, query, searchMode);
      return { error: errorData.message };
    }

    if (response.status === 500) {
      const errorData = await response.json();
      throw new Error(errorData.message);
    }
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return { id: data.jobId };
  } catch (error) {
    console.error("Error fetching UUID:", error);
    return { error: "An error occurred during search. Please try again." };
  }
};

export const fetchResults = async (id) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/job/${id}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching results:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

export const isUserSubscribed = async () => {
  const idToken = await getToken();

  try {
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/subscriptionStatus`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching subscription status", error);
    throw error;
  }
};

export const continueSubscription = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/continueSubscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error continuing subscription", error);
    throw error;
  }
};

// Screening functions
export const uploadScreeningFile = async (file) => {
  try {
    const idToken = await getToken();
    const formData = new FormData();
    formData.append("file", file);

    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/upload`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Upload failed");
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading screening file:", error);
    throw error;
  }
};

export const createScreeningJob = async (jobData) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/jobs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(jobData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create screening job");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating screening job:", error);
    throw error;
  }
};

export const getScreeningJobs = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/jobs`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch screening jobs");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching screening jobs:", error);
    throw error;
  }
};

export const getScreeningJob = async (jobId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/jobs/${jobId}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch screening job");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching screening job:", error);
    throw error;
  }
};

export const downloadScreeningResults = async (jobId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/jobs/${jobId}/download`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get download URL");
    }

    const data = await response.json();
    return data.downloadUrl;
  } catch (error) {
    console.error("Error getting download URL:", error);
    throw error;
  }
};

export const uploadPdfScreeningFiles = async (files) => {
  try {
    const idToken = await getToken();
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/upload-pdfs`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Upload failed");
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading PDF screening files:", error);
    throw error;
  }
};

export const createPdfScreeningJob = async (jobData) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/pdf-jobs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(jobData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create PDF screening job");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating PDF screening job:", error);
    throw error;
  }
};

export const rerunScreeningJob = async (jobId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/jobs/${jobId}/rerun`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to rerun screening job");
    }

    return await response.json();
  } catch (error) {
    console.error("Error rerunning screening job:", error);
    throw error;
  }
};

export const getUserScreeningFiles = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/files`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch user screening files");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching user screening files:", error);
    throw error;
  }
};

export const deleteScreeningFile = async (file) => {
  try {
    const idToken = await getToken();

    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/files`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json", // Good practice, though no body is sent now.
        },
        body: JSON.stringify({ file }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete screening file");
    }
  } catch (error) {
    console.error("Error deleting screening file:", error);
    throw error;
  }
};

export const fetchLastExtractedData = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/extract/last`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Failed to extract data");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching last extracted data", error);
    throw error;
  }
};

export const extractData = async (extractionInput) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/extract`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-type": "application/json",
        },
        body: JSON.stringify(extractionInput),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to extract data");
    }

    return await response.json();
  } catch (error) {
    console.error("Error extracting data", error);
    throw error;
  }
};

export const cancelSubscription = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/cancelSubscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error cancelling subscription", error);
    throw error;
  }
};
export const fetchSearchHistory = async () => {
  try {
    const idToken = await getToken();

    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/searchHistory`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching search results", error);
    throw error;
  }
};
export const sendEmail = async (email, id) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/send-email/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email }),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching results:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

export const translateReport = async (targetLanguage, jobId) => {
  try {
    const idToken = await getToken();

    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/translate/${jobId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          targetLanguage,
        }),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return {
      originalTranslatedSummary: data.originalTranslatedSummary,
      simplifiedTranslatedSummary: data.simplifiedTranslatedSummary,
      translatedArticles: data.translatedArticles,
      translatedSimplifiedArticles: data.translatedSimplifiedArticles,
    };
  } catch (error) {
    console.error("Unable to translate text", error);
    throw error;
  }
};

export const createUserAccount = async (email, userId) => {
  try {
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/signup`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, userId }),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
  } catch (error) {
    console.error("Error sending token:", error);
    throw error;
  }
};

export async function upgradeToPremium(planType = "monthly") {
  try {
    const idToken = await getToken();
    const currentPath = window.location.pathname + window.location.search;
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/premium`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          redirectUrl: `/thankyou?redirect=${encodeURIComponent(currentPath)}`,
          planType,
        }),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    window.location.href = data.checkoutUrl;
  } catch (error) {
    console.error("Error fetching results:", error);
    throw error;
  }
}

export async function changePlan(planType) {
  const idToken = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/user/changePlan`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ planType }),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Failed to change plan");
  return data;
}

export function generateKey() {
  const uniqueId = uuid();
  const secret = process.env.REACT_APP_SECRET_KEY;
  const salt = lib.WordArray.random(128 / 8);

  const key = PBKDF2(secret, salt, {
    keySize: 256 / 32,
    iterations: 1000,
    hasher: algo.SHA256,
  });

  const iv = lib.WordArray.random(128 / 8);

  const ciphertext = AES.encrypt(uniqueId, key, {
    iv: iv,
    mode: mode.CBC,
    padding: pad.Pkcs7,
  });

  return enc.Base64.stringify(salt.concat(iv).concat(ciphertext.ciphertext));
}

export const fetchLanguages = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/languages`,
      {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not okay");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching languages", error);
    throw error;
  }
};

export const uploadFiles = async (organizationId, files) => {
  try {
    const idToken = await getToken();
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/file/${organizationId}/upload`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error uploading files:", error);
    throw error;
  }
};

export const createOrganization = async (organizationName) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/organization`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ organizationName }),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

export const fetchStudyDesigns = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/study-designs`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data.studyDesigns;
  } catch (error) {
    console.error("Error fetching study designs:", error);
    throw error;
  }
};

export const fetchUserOrganization = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/organizations`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null; // No organization found
      }
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching user's organization:", error);
    throw error;
  }
};

export const fetchOrganizationFiles = async (organizationId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/file/${organizationId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching organization files:", error);
    throw error;
  }
};

export const getSignedFileUrl = async (organizationId, fileId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/file/${organizationId}/${fileId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null; // File not found
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error("Error fetching signed file URL:", error);
    throw error;
  }
};

export const deleteOrganizationFile = async (organizationId, fileId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/file/${organizationId}/${fileId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) {
        return { error: "File not found" };
      }
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error deleting organization file:", error);
    throw error;
  }
};

export const downloadOrganizationFile = async (organizationId, fileId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/file/${organizationId}/${fileId}/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) {
        return { error: "File not found" };
      }
      throw new Error("Network response was not ok");
    }
    // Get filename from Content-Disposition header
    const disposition = response.headers.get("Content-Disposition");
    let filename = "downloaded_file";
    if (disposition && disposition.indexOf("filename=") !== -1) {
      filename = disposition.split("filename=")[1].replace(/"/g, "");
    }
    const blob = await response.blob();
    // Create a link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error("Error downloading organization file:", error);
    throw error;
  }
};

export const sendInvitation = async (organizationId, email, role) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/organization/${organizationId}/invitations`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email, role }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error sending invitation:", error);
    throw error;
  }
};

export const acceptInvitation = async (token) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/organization/invitations/${token}/accept`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error accepting invitation:", error);
    throw error;
  }
};

export const fetchOrganizationInvitations = async (organizationId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/organization/${organizationId}/invitations`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching organization invitations:", error);
    throw error;
  }
};

export const fetchUserOrganizationRole = async (organizationId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/organization/role?organizationId=${organizationId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Network response was not ok");
    }
    const data = await response.json();
    return data.role;
  } catch (error) {
    console.error("Error fetching user organization role:", error);
    throw error;
  }
};

export const removeOrganizationInvitation = async (invitationId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/organization/invitations/${invitationId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error removing organization invitation:", error);
    throw error;
  }
};

export const deleteOrganization = async (organizationId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/organization/${organizationId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error deleting organization:", error);
    throw error;
  }
};

export const getScheduledJobs = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/scheduledSearch`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch scheduled jobs");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching scheduled jobs:", error);
    throw error;
  }
};

export const getScheduledJobHistory = async (jobId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/scheduledSearch/${jobId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch scheduled job history");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching scheduled job history:", error);
    throw error;
  }
};

export const getLiteratureReviewHistory = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/history`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch literature review history");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching literature review history:", error);
    throw error;
  }
};

export const createCriticalAppraisalJob = async (jobConfig) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/screening/critical-appraisal`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(jobConfig),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to create critical appraisal job"
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating critical appraisal job:", error);
    throw error;
  }
};

export const deleteScheduledJob = async (jobId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/scheduledSearch/${jobId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete scheduled job");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting scheduled job:", error);
    throw error;
  }
};

export const updateScheduledJob = async (jobId, jobData) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/scheduledSearch/${jobId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(jobData),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update scheduled job");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating scheduled job:", error);
    throw error;
  }
};

export const saveUserSearchTerms = (
  question,
  filters,
  pico,
  query,
  searchMode
) => {
  const searchTerms = {
    question,
    filters,
    pico,
    query,
    searchMode,
    timeStamp: Date.now(),
  };
  sessionStorage.setItem("pendingFormState", JSON.stringify(searchTerms));
};

export const getUserSearchTerms = () => {
  try {
    const savedState = sessionStorage.getItem("pendingFormState");
    if (savedState) {
      const searchTerms = JSON.parse(savedState);
      // check if the state is not too old, i.e. within one hour
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - searchTerms.timeStamp < oneHour) {
        return searchTerms;
      } else {
        clearFormState();
      }
    }
  } catch (error) {
    console.error("Error retrieving user search terms: ", error);
  }
  return null;
};

export const clearFormState = () => {
  sessionStorage.removeItem("pendingFormState");
};

export const simplifyResult = async (jobId, selectedLanguage) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/simplify/${jobId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ selectedLanguage }),
      }
    );
    if (!response) {
      throw new Error("Failed to simplify result");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error simplifying job:", error);
    throw error;
  }
};

export const getUserDetails = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/info`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};

export const fetchEnterprises = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/enterprise/details`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch enterprises");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching enterprise details:", error);
    throw error;
  }
};

export const fetchEnterpriseById = async (enterpriseId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/enterprise/${enterpriseId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch enterprise");
    }
    const data = await response.json();
    if (data.enterprise) {
      return data.enterprise;
    }
    return data;
  } catch (error) {
    console.error("Error fetching enterprise by id:", error);
    throw error;
  }
};

export const createEnterprise = async (name, capacity, expiryDate) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/enterprise/createEnterprise`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, capacity, expiryDate }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create enterprise");
    }
    const data = await response.json();
    return data.enterprise || data;
  } catch (error) {
    console.error("Error creating enterprise:", error);
    throw error;
  }
};

export const updateEnterprise = async (enterpriseId, updateData) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/enterprise/${enterpriseId}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update enterprise");
    }
  } catch (error) {
    console.error("Error updating enterprise:", error);
    throw error;
  }
};

export const createEnterpriseUser = async (enterpriseId, createData) => {
  try {
    const idToken = await getToken();

    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/enterprise/${enterpriseId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: createData.email,
          isAdmin: createData.isAdmin,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create enterprise user");
    }
  } catch (error) {
    console.error("Error creating enterprise user:", error);
    throw error;
  }
};

export const deleteEnterpriseUser = async (enterpriseId, enterpriseUserId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/enterprise/${enterpriseId}/users/${enterpriseUserId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete enterprise user");
    }
  } catch (error) {
    console.error("Error deleting enterprise user:", error);
    throw error;
  }
};

export const initiatePaperSourceSearch = async (question) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.message || errorData.error || "Failed to gather sources";
      throw new Error(errorMessage);
    }
    const data = await response.json();
    return { id: data.jobId, litreviewtitle: data.litreviewtitle };
  } catch (error) {
    console.error("Error initiating paper source search", error);
    throw error;
  }
};

export const fetchSources = async (id) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/${id}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching sources", error);
    throw error;
  }
};

export const updateLitreviewtitle = async (reviewId, litreviewtitle) => {
  const id_Token = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/review/${reviewId}/litreviewtitle`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${id_Token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ litreviewtitle }),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data?.message || data?.error || "Failed to update title");
    err.status = response.status;
    throw err;
  }
  return data; // { message, id, litreviewtitle }
};

export const saveAddedArticles = async (reviewId, articleUniqueIds) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/${reviewId}/added-articles`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ articleUniqueIds }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.message || errorData.error || "Failed to save added articles";
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Error saving added articles", error);
    throw error;
  }
};

export const fetchContent = async (type) => {
  try {
    const headers = {
      "Content-type": "application/json",
    };

    // Check if user is logged in by trying to get auth session token
    // Only adding Authorization header if user is actually authenticated
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (error) { }

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/content?type=${type}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching FAQs", error);
    throw error;
  }
};

export const createContent = async (question, answer, isActive = true, type) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/content`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question, answer, isActive, type }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create FAQ");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating FAQ", error);
    throw error;
  }
};

export const updateContent = async (question, answer, isActive, id, type) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/content/${id}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question, answer, isActive, type }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update FAQ");
    }
  } catch (error) {
    console.error("Error updating FAQ", error);
    throw error;
  }
};

export const deleteContent = async (id) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/content/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete FAQ");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting FAQ", error);
    throw error;
  }
};

export const updateEmailPreference = async () => {
  try {
    const idToken = await getToken()
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/unsubscribe`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      }
    })
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.message || errorData.error || "Failed to gather sources";
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Error updating sources", error)
    throw error;
  }
}

export const proceedLargeSearch = async (id) => {
  const idToken = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/review/proceed-large-search/${id}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || "Failed to proceed with large search");
  }
  return response.json();
};

export const updateSources = async (id, question, query, filters = {}) => {
  try {
    const id_Token = await getToken()
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${id_Token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ question, query, filters })
    })

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.message || errorData.error || "Failed to update search";
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Error updating email preference", error)
    throw error;
  }
}

export const deleteAccount = async () => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/user/account`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.message || errorData.error || "Failed to delete account";
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Error deleting account", error);
    throw error;
  }
}

export const submitArticleIdsForAbstractScreening = async (id, articleUniqueIds) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/createAbstractSuggestedColumns/${id}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },

        body: JSON.stringify({ articleUniqueIds })
      }
    )

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.message || errorData.error || "Failed to screen abstracts";
      throw new Error(errorMessage);
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error submitting articles IDs for screening", error)
    throw error;
  }
}

export const getAbstractScreeningStatus = async (id) => {
  const id_Token = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/review/abstractScreeningStatus/${id}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${id_Token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || errorData.error || "Failed to fetch screening status";
    throw new Error(errorMessage);
  }
  return response.json();
};

export const upsertAbstractScreeningColumn = async (reviewId, { columnName, screeningQuestion, columnId }) => {
  try {
    const id_Token = await getToken()
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/upsertAbstractScreeningColumn/${reviewId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          columnName,
          screeningQuestion,
          columnId
        }),
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage =
        errorData.message || errorData.error || "Failed to add screening column"
      throw new Error(errorMessage)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error creating abstract screening column", error)
    throw error
  }
}

export const deleteAbstractScreeningColumn = async (reviewId, columnId) => {
  try {
    const id_Token = await getToken()
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/deleteAbstractScreeningColumn/${reviewId}/${columnId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage =
        errorData.message || errorData.error || "Failed to delete screening column"
      throw new Error(errorMessage)
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error deleting abstract screening column:", error)
    throw error
  }
}

export const getExtractionStatus = async (id) => {
  const id_Token = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/review/extractionStatus/${id}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${id_Token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || errorData.error || "Failed to fetch extraction status";
    throw new Error(errorMessage);
  }
  return response.json();
};

export const getExtractionData = async (id, includedArticleIds) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/getExtractionData/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${id_Token}`
        },
        body: JSON.stringify({ includedArticleIds })
      }
    )
    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage = errorData.message || errorData.error || "Failed to get extraction data";
      throw new Error(errorMessage);
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error getting extraction data", error)
    throw error;
  }
}

export const createExtractionNewColumn = async (reviewId, { columnName, includedArticleIds, instructions, answerStructure }) => {
  try {
    const id_Token = await getToken()
    const body = {
      columnName,
      includedArticleIds,
      answerStructure: answerStructure || "any",
    }
    const trimmed = instructions != null ? String(instructions).trim() : ""
    if (trimmed) {
      body.instructions = trimmed
    }
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/extract-new/${reviewId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage =
        errorData.message || errorData.error || "Failed to add extraction column"
      throw new Error(errorMessage)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error creating extraction column", error)
    throw error
  }
}

export const reextractColumn = async (reviewId, { columnId, columnName, instructions, includedArticleIds, answerStructure }) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/reextractColumn/${reviewId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          columnId,
          columnName,
          includedArticleIds,
          instructions: instructions != null ? String(instructions).trim() : "",
          answerStructure: answerStructure || "any",
        }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || "Failed to re-extract column");
    }
    return await response.json();
  } catch (error) {
    console.error("Error re-extracting column:", error);
    throw error;
  }
};

export const deleteExtractionColumn = async (reviewId, columnId) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/deleteExtractionColumn/${reviewId}/${columnId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${id_Token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || "Failed to delete extraction column");
    }
    return await response.json();
  } catch (error) {
    console.error("Error deleting extraction column:", error);
    throw error;
  }
};

export const extractDataForIncludedArticles = async (id, includedArticleIds, columns, fullTextArticleIds = null) => {
  try {
    const id_Token = await getToken();
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/extractData/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${id_Token}`
        },

        body: JSON.stringify({ includedArticleIds, columns, fullTextArticleIds })
      }
    )
    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage =
        errorData.message || errorData.error || "Failed to extract data";
      throw new Error(errorMessage);
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error extracting data", error)
    throw error;
  }
}

export const generateReport = async (id) => {
  try {
    const id_Token = await getToken()
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/synthesize/${id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${id_Token}`
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage =
        errorData.message || errorData.error || "Failed to synthesize data";
      throw new Error(errorMessage);
    }
    const data = await response.json()
    if (response.status === 202) {
      return { status: "processing", ...data }
    }
    return data
  } catch (error) {
    console.error("Error synthesizing report ", error)
    throw error;
  }
}

export const getSynthesisStatus = async (id) => {
  const id_Token = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/review/synthesisStatus/${id}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${id_Token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || errorData.error || "Failed to fetch report status";
    throw new Error(errorMessage);
  }
  return response.json();
};

export const generateResearchGap = async (id) => {
  try {
    const id_Token = await getToken()
    const response = await authorizedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/review/researchGap/${id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${id_Token}`
      }
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage =
        errorData.message || errorData.error || "Failed to generate research gap analysis";
      throw new Error(errorMessage);
    }
    return response.json()
  } catch (error) {
    console.error("Error generating research gap analysis", error)
    throw error;
  }
}

export const getResearchGapStatus = async (id) => {
  const id_Token = await getToken();
  const response = await authorizedFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/review/researchGapStatus/${id}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${id_Token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || errorData.error || "Failed to fetch research gap status";
    throw new Error(errorMessage);
  }
  return response.json();
};

export const translateLitReviewReport = async (jobId, targetLanguage) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/translate/${jobId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ targetLanguage }),
      }
    );
    if (!response.ok) throw new Error("Failed to translate report");
    return response.json();
  } catch (error) {
    console.error("Error translating lit review report:", error);
    throw error;
  }
};

export const simplifyLitReviewReport = async (jobId) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/simplify/${jobId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to simplify report");
    return response.json();
  } catch (error) {
    console.error("Error simplifying lit review report:", error);
    throw error;
  }
};

export const startFullTextScreening = async (id, articles) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/fullTextScreening/${id}`,
      {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ articles }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Failed to start full-text screening"
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error starting full-text screening:", error);
    throw error;
  }
};

export const getLiteratureReview = async (id) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/${id}`,
      {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch literature review");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching literature review:", error);
    throw error;
  }
};

export const getPdfFileUrls = async (dois) => {
  try {
    const idToken = await getToken();
    const response = await authorizedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/review/pdfFileUrls`,
      {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ dois }),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to get PDF file URLs");
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting PDF file URLs:", error);
    throw error;
  }
};
